// mainAlgorithm.js
import { createAnalysisSegments, detectSpeechSegments, detectPausesAndMarkUnvoiced } from './core/segmentation.js';
import { findLocalMaxima } from './core/peakDetection.js';
import { calculateNoteDurationsWithinSegment, createSegmentNote, getNoteType } from './core/noteGeneration.js';
import { mapToStaffPosition } from './core/noteMapping.js'; // ADD THIS
import { detectTails, createTailNotes } from './tailDetection/tailDetection.js';

// Enhanced main algorithm with simple tail detection
export const generateMusicalNotesWithTails = (pitch, intensity, segment, pitch1stDerivative, broadcastTime = 0) => {
  if (!pitch?.xs || !pitch?.ys || pitch.xs.length === 0) return [];
  if (!intensity?.xs || !intensity?.ys || intensity.xs.length === 0) return [];
  
  const totalSegmentDuration = segment ? segment.end - segment.start : 0;
  const currentTime = broadcastTime;

  // Get global statistics
  const allVoicedPitches = pitch.ys.filter(p => p > 0);
  if (allVoicedPitches.length === 0) return [];

  const allIntensities = intensity.ys.filter(i => i > 0);
  if (allIntensities.length === 0) return [];
  
  const globalMinIntensity = Math.min(...allIntensities);
  const globalMaxIntensity = Math.max(...allIntensities);

  // Create analysis segments
  const { pitchSegments, intensitySegments } = createAnalysisSegments(pitch, intensity);
  if (pitchSegments.length === 0) return [];

  // Detect speech segments
  const speechSegments = detectSpeechSegments(pitch, intensity, 0.3);
  if (speechSegments.length === 0) return [];

  const allNotes = [];
  
  console.log(`Processing ${speechSegments.length} speech segments for tail detection`);
  
  // Process each speech segment
  speechSegments.forEach((speechSegment, segmentIndex) => {
    // Add intensity points to speech segment for tail detection
    speechSegment.intensityPoints = intensitySegments.filter(i => 
      i.time >= speechSegment.start && i.time <= speechSegment.end
    );
    
    const segmentPitchPoints = pitchSegments.filter(p => 
      p.time >= speechSegment.start && p.time <= speechSegment.end
    );
    
    const segmentIntensityPoints = intensitySegments.filter(i => 
      i.time >= speechSegment.start && i.time <= speechSegment.end
    );
    
    if (segmentPitchPoints.length < 3) {
      createSegmentNote(speechSegment, segmentIndex, allNotes, totalSegmentDuration, currentTime, globalMinIntensity, globalMaxIntensity);
      return;
    }
    
    // Find local maxima
    const localMaxima = findLocalMaxima(segmentPitchPoints, segmentIntensityPoints, globalMaxIntensity);
    
    // Detect tails after last local maxima
 const tails = detectTails(speechSegment, segmentPitchPoints, segmentIntensityPoints, globalMaxIntensity, pitch1stDerivative);
    
    console.log(`Segment ${segmentIndex}: ${localMaxima.length} local maxima, ${tails.length} tails`);
    
    if (localMaxima.length > 0) {
      const validMaxima = localMaxima.filter(maxima => maxima.hasStrongIntensity);

      if (validMaxima.length > 0) {
        const noteDurations = calculateNoteDurationsWithinSegment(validMaxima, speechSegment.start, speechSegment.end);
        
        // Create notes for local maxima
        validMaxima.forEach((maxima, maximaIndex) => {
          const durationInfo = noteDurations[maximaIndex];
          const cappedDuration = Math.min(durationInfo.duration, 1.2);
          const absolutePosition = (maxima.time / totalSegmentDuration) * 100;
          const actualPitch = maxima.pitch;
          
          // These functions are now imported
          const { staff, noteLevel, musicalNote, frequency, line } = mapToStaffPosition(actualPitch);
          const { noteType, stemHeight, noteHeadClass } = getNoteType(
            cappedDuration, 
            maxima.intensity, 
            globalMaxIntensity, 
            true
          );
          
          const stemDirection = line < 2 ? 'up' : 'down';
          const isActive = currentTime >= durationInfo.start && currentTime <= durationInfo.end;

          allNotes.push({
            staff,
            noteLevel,
            noteType,
            stemHeight,
            stemDirection,
            noteHeadClass,
            position: absolutePosition,
            duration: cappedDuration,
            startTime: durationInfo.start,
            endTime: durationInfo.end,
            isActive,
            pitch: actualPitch,
            intensity: maxima.intensity,
            musicalNote,
            targetFrequency: frequency,
            linePosition: line,
            hasStress: true,
            isLocalMaxima: true,
            maximaIndex,
            prominence: maxima.prominence,
            time: maxima.time,
            isVoiced: true,
            segmentIndex
          });
        });
        
        // Add tail notes
        if (tails.length > 0) {
          console.log(`Adding tail notes after local maxima`);
          tails.forEach(tail => {
            createTailNotes(tail, segmentIndex, allNotes, totalSegmentDuration, currentTime, globalMaxIntensity);
          });
        }
      } else {
        // No valid maxima but we might have tails
        if (tails.length > 0) {
          console.log(`No valid maxima, using tail notes as primary`);
          tails.forEach(tail => {
            createTailNotes(tail, segmentIndex, allNotes, totalSegmentDuration, currentTime, globalMaxIntensity);
          });
        } else {
          createSegmentNote(speechSegment, segmentIndex, allNotes, totalSegmentDuration, currentTime, globalMinIntensity, globalMaxIntensity);
        }
      }
    } else {
      // No local maxima - use segment note
      createSegmentNote(speechSegment, segmentIndex, allNotes, totalSegmentDuration, currentTime, globalMinIntensity, globalMaxIntensity);
    }
  });

  // Sort notes by time and add pauses
  let sortedNotes = allNotes.sort((a, b) => a.time - b.time);
  sortedNotes = detectPausesAndMarkUnvoiced(sortedNotes, pitch, intensity, 0.3);
  
  const tailNoteCount = sortedNotes.filter(n => n.isRisingTail).length;
  console.log(`Generated ${sortedNotes.length} notes with ${tailNoteCount} tail notes`);
  
  return sortedNotes;
};

// Original main algorithm for backward compatibility
export const generateMusicalNotes = (pitch, intensity, segment, broadcastTime = 0) => {
  return generateMusicalNotesWithTails(pitch, intensity, segment, broadcastTime);
};